import React, { useEffect, useState, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import TableContainer from "../../../components/Common/TableContainer"
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    Label,
    FormFeedback,
    Input,
    Form,
} from "reactstrap"
import toast from "react-hot-toast"
import Breadcrumbs from "components/Common/Breadcrumb"
import Select from "react-select"
import * as Yup from "yup"
import { useFormik } from "formik"
import Spinners from "components/Common/Spinner"
import DeleteModal from "components/Common/DeleteModal"
import {
    createBookingSlot,
    getAllEvents,
    getAllTicketGroup,
    getBookingSlotByEventSlug,
    updateBookingSlot,
    deleteBookingSlot
} from "api";

const BookingSlot = () => {
    const navigate = useNavigate()

    //meta title
    document.title = "Booking Slot | EventoX"

    const [isLoading, setIsLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [events, setEvents] = useState([])
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [ticketGroupList, setTicketGroupList] = useState([]);
    const [selectedSlugId, setSelectedSlugId] = useState("");
    const [slots, setSlots] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [currentSlot, setCurrentSlot] = useState(null);
    const [statusOptions,setStatusOptions]=useState([
        { value: "active", label: "Open" },
        { value: "inactive", label: "Closed" },
    ])

    const filteredTicketGroups = ticketGroupList.filter(
        group => group.slugId === selectedSlugId
    );

    const getEvents = async () => {
        try {
            const response = await getAllEvents();
            setEvents(response);
        } catch (error) {
            console.log(error);
        }
    };
    const eventOptions = useMemo(() => {
        return events.map(event => ({
            value: event.slugId,
            label: event.eventName,
        }))
    }, [events])

    const getTicketGroups = async () => {
        try {
            const response = await getAllTicketGroup();
            if (response) {
                let filtered = response.filter((item) => item.isSlotRequired === true);
                setTicketGroupList(filtered);
            } else {
                setTicketGroupList([]);
            }
        } catch (error) {
            console.error("Failed to fetch ticket groups:", error);
        }
    };

    const getSlotsByEvents = async (slugId) => {
        try {
            const res = await getBookingSlotByEventSlug(slugId);
            if (res) {
                setSlots(res)
            }
        }
        catch (err) {
            console.log('error in fetching slots by events', err)
        }
    }

    useEffect(() => {
        getEvents();
        getTicketGroups();
    }, []);

    useEffect(() => {
        if (selectedEvent && selectedEvent.value) {
            getSlotsByEvents(selectedEvent.value);
        }
    }, [selectedEvent])

    const toggle = () => {
        setModal(!modal);
        if (!modal) {
            setIsEdit(false);
            setCurrentSlot(null);
            validation.resetForm();
        }
    }

    const toggleDeleteModal = () => {
        setDeleteModal(!deleteModal);
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            slugId: currentSlot?.slugId || "",
            ticketGroupId: currentSlot?.ticketGroupId || "",
            slotName: currentSlot?.slotName || "",
            totalCount: currentSlot?.totalCount || "",
            status: currentSlot?.status || "",
        },
        validationSchema: Yup.object({
            slugId: Yup.string().required("Please select event"),
            ticketGroupId: Yup.string().required("Please select ticket group"),
            slotName: Yup.string().required("Please enter slot name"),
            totalCount: Yup.number().required("Please enter total count"),
            status: Yup.string().required("Please select status"),
        }),
        onSubmit: async (values) => {
            setIsLoading(true);
            try {
                let response;
                if (isEdit && currentSlot) {
                    response = await updateBookingSlot(currentSlot.id, values);
                } else {
                    response = await createBookingSlot(values);
                }

                if (response) {
                    setIsLoading(false);
                    setModal(false);
                    toast.success(`Booking slot ${isEdit ? 'updated' : 'added'} successfully`);
                    if (selectedEvent && selectedEvent.value) {
                        getSlotsByEvents(selectedEvent.value);
                    }
                } else {
                    setIsLoading(false);
                    toast.error("Something went wrong");
                }
            } catch (error) {
                setIsLoading(false);
                toast.error(error.message || "Something went wrong");
            }

            setSelectedSlugId("");
            validation.resetForm();
            toggle();
        },
    });

    const handleEditClick = (slot) => {
        setCurrentSlot(slot);
        setIsEdit(true);
        setSelectedSlugId(slot.slugId);
        setModal(true);
    };

    const handleDeleteClick = (slot) => {
        setCurrentSlot(slot);
        toggleDeleteModal();
    };

    const handleDelete = async () => {
        if (!currentSlot) return;
        setIsLoading(true);
        try {
            const response = await deleteBookingSlot(currentSlot.id);
            if (response) {
                toast.success("Booking slot deleted successfully");
                if (selectedEvent && selectedEvent.value) {
                    getSlotsByEvents(selectedEvent.value);
                }
            } else {
                toast.error("Something went wrong");
            }
        } catch (error) {
            toast.error(error.message || "Something went wrong");
        } finally {
            setIsLoading(false);
            toggleDeleteModal();
        }
    };

    const columns = useMemo(
        () => [
            {
                header: "Slot Name",
                accessorKey: "slotName",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Ticket Group Name",
                accessorKey: "groupName",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Event Slug",
                accessorKey: "slugId",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Total Count",
                accessorKey: "totalCount",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Sold Count",
                accessorKey: "bookedCount",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Status",
                accessorKey: "status",
                enableColumnFilter: false,
                enableSorting: true,
                cell: cell => {
                    return (
                        <div>
                            {cell.getValue() === "active" ? (
                                <span className="badge badge-soft-primary font-size-11 m-1">
                                    {cell.getValue()}
                                </span>
                            ) : (
                                <span className="badge badge-soft-danger font-size-11 m-1">
                                    {cell.getValue()}
                                </span>
                            )}
                        </div>
                    )
                },
            },
            {
                header: 'Action',
                cell: (cellProps) => {
                    return (
                        <div className="d-flex gap-3">
                            <div
                                className="text-success btn-hover"
                                onClick={() => handleEditClick(cellProps.row.original)}
                            >
                                <i className="mdi mdi-pencil font-size-18" id="edittooltip" />
                            </div>
                            <div
                                className="text-danger btn-hover"
                                onClick={() => handleDeleteClick(cellProps.row.original)}
                            >
                                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
                            </div>
                        </div>
                    );
                }
            },
        ],
        []
    );

    const handleClickNew = () => {
        toggle();
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem="Booking Slots" />

                    <Row>
                        {isLoading ? (
                            <Spinners setLoading={setIsLoading} />
                        ) : (
                            <Col lg="12">
                                <Card>
                                    <CardBody>
                                        <Row className="mb-3">
                                            <Col xl="5">
                                                <div className="col-md-12">
                                                    <Select
                                                        value={selectedEvent}
                                                        placeholder="Select Event"
                                                        onChange={data => setSelectedEvent(data)}
                                                        options={[
                                                            { value: "ALL", label: "All Events" },
                                                            ...events.map(event => ({
                                                                value: event.slugId,
                                                                label: event.eventName,
                                                            })),
                                                        ]}
                                                        width="100%"
                                                        className="select2-selection"
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <TableContainer
                                                columns={columns}
                                                data={slots || []}
                                                isGlobalFilter={true}
                                                isPagination={true}
                                                SearchPlaceholder="Search..."
                                                isCustomPageSize={true}
                                                isAddButton={true}
                                                handleUserClick={handleClickNew}
                                                buttonClass="btn btn-success btn-rounded waves-effect waves-light addContact-modal mb-2"
                                                buttonName="New Booking Slot"
                                                tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                                theadClass="table-light"
                                                paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                                pagination="pagination"
                                            />
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}

                        <Modal isOpen={modal} toggle={toggle}>
                            <ModalHeader toggle={toggle} tag="h4">
                                {isEdit ? "Edit Booking Slot" : "Add New Booking Slot"}
                            </ModalHeader>
                            <ModalBody>
                                <Form
                                    onSubmit={e => {
                                        e.preventDefault()
                                        validation.handleSubmit()
                                        return false
                                    }}
                                >
                                    <Row>
                                        <Col xs={12}>
                                        <div className="mb-3">
                                                <Select
                                                    name="slugId"
                                                    options={eventOptions}
                                                    placeholder="Select Event"
                                                    value={eventOptions.find(opt => opt.value === validation.values.slugId) || null}
                                                    onChange={option => {
                                                    const selectedValue = option?.value || "";
                                                    validation.setFieldValue("slugId", selectedValue);     
                                                    setSelectedSlugId(selectedValue);                       
                                                    validation.setFieldValue("ticketGroupId", "");          
                                                    }}
                                                    onBlur={() => validation.setFieldTouched("slugId", true)}
                                                    classNamePrefix="rs"
                                                    className={validation.touched.slugId && validation.errors.slugId ? "is-invalid" : ""}
                                                    styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                                                />

                                                {validation.touched.slugId && validation.errors.slugId ? (
                                                    <FormFeedback type="invalid" className="d-block">
                                                    {validation.errors.slugId}
                                                    </FormFeedback>
                                                ) : null}
                                                </div>
                                            {selectedSlugId && (
                                                <div className="mb-3">
                                                    <Label className="form-label">Select Ticket Group</Label>
                                                    <select
                                                        name="ticketGroupId"
                                                        className="form-select"
                                                        placeholder="Select Ticket Group"
                                                        value={validation.values.ticketGroupId}
                                                        onChange={e =>
                                                            validation.setFieldValue("ticketGroupId", e.target.value)
                                                        }
                                                        onBlur={validation.handleBlur}
                                                    >
                                                        <option value="">Select Ticket Group</option>
                                                        {filteredTicketGroups.map(type => (
                                                            <option key={type.id} value={type.id}>
                                                                {type.groupName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {validation.touched.ticketGroupId && validation.errors.ticketGroupId ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.ticketGroupId}
                                                        </FormFeedback>
                                                    ) : null}
                                                </div>)}
                                            <div className="mb-3">
                                                <Label className="form-label">Slot Name</Label>
                                                <Input
                                                    name="slotName"
                                                    type="text"
                                                    placeholder="Slot Name"
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.slotName || ""}
                                                    invalid={
                                                        validation.touched.slotName && validation.errors.slotName
                                                            ? true
                                                            : false
                                                    }
                                                />
                                                {validation.touched.slotName && validation.errors.slotName ? (
                                                    <FormFeedback type="invalid">
                                                        {validation.errors.slotName}
                                                    </FormFeedback>
                                                ) : null}
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label">Total Slot Available</Label>
                                                <Input
                                                    name="totalCount"
                                                    type="number"
                                                    placeholder="Total Slot Available"
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.totalCount || ""}
                                                    invalid={
                                                        validation.touched.totalCount && validation.errors.totalCount
                                                            ? true
                                                            : false
                                                    }
                                                />
                                                {validation.touched.totalCount && validation.errors.totalCount ? (
                                                    <FormFeedback type="invalid">
                                                        {validation.errors.totalCount}
                                                    </FormFeedback>
                                                ) : null}
                                            </div>
                                            <div className="mb-3">
                                        <Label htmlFor="project-status-input">Status</Label>
                                        <Select
                                            inputId="project-status-input"
                                            name="status"
                                            options={statusOptions}
                                            placeholder="Select Status"
                                            value={statusOptions.find(opt => opt.value === validation.values.status) || null}
                                            onChange={option => validation.setFieldValue("status", option?.value || "")}
                                            onBlur={() => validation.setFieldTouched("status", true)}
                                            classNamePrefix="rs"
                                            className={validation.touched.status && validation.errors.status ? "is-invalid" : ""}
                                            styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                                        />

                                        {validation.touched.status && validation.errors.status ? (
                                            <FormFeedback type="invalid" className="d-block">
                                            {validation.errors.status}
                                            </FormFeedback>
                                        ) : null}
                                        </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <div className="text-end">
                                                <button
                                                    type="submit"
                                                    className="btn btn-success save-user"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? "Loading...." : "Save"}
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            </ModalBody>
                        </Modal>

                        <DeleteModal
                            show={deleteModal}
                            onDeleteClick={handleDelete}
                            onCloseClick={toggleDeleteModal}
                        />
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default withRouter(BookingSlot)